import {Injectable} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BaseSiteService, GlobalMessageType, TranslationService, UserIdService} from '@spartacus/core';
import {DelegoRapidPayThreeDSecureInfoWithDelegoToken} from '@app/model/delego-rapidpay/delego-rapidpay-authenticate';
import {DelegoRapidPayAuthorization} from '@app/model/delego-rapidpay/delego-rapidpay-authorization';
import {DelegoPaymentState} from '../../store/payment-state';
import {PaymentError} from '../../error-handling/payment-error.enum';
import {Router} from '@angular/router';
import {RefreshCartService} from '@app/checkout/services/refresh-cart.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpApiService} from '@dcp/configurator';
import {DelegoAuthenticationValidationService} from '../validation/delego-authentication-validation.service';
import {PaymentErrorMessage} from '../../error-handling/payment-error-message.enum';
import {CommonError} from '@app/model/error/common-error.model';
import {Cart} from '@spartacus/cart/base/root';
import {IDcpGlobalErrorMessage} from '@app/core/models/interfaces/error';
import {IDcpGlobalErrorMessageButton} from '@app/core/models/interfaces/error-message-button';
import {PlaceOrderRequestBody} from '@app/model/order/dcp-place-order-body.model';
import * as fromDelego from '@app/payment/services/store/reducers/delego.reducer';
import {Store} from '@ngrx/store';

export const CC_LIMIT_EXCEEDED_RESPONSE_CODE: string = '275';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DelegoPaymentErrorService {
  readonly FAILURE_STATUSES = ['N', 'R', 'U'];

  constructor(
    private translationService: TranslationService,
    private router: Router,
    private refreshCartService: RefreshCartService,
    private userIdService: UserIdService,
    private activeCartService: DcpActiveCartService,
    private baseSiteService: BaseSiteService,
    private dcpApiService: DcpApiService,
    private delegoAuthenticationValidationService: DelegoAuthenticationValidationService,
    private store: Store
  ) {}

  savePaymentError(body: PlaceOrderRequestBody) {
    combineLatest([
      this.userIdService.getUserId(),
      this.activeCartService.getActiveCart(),
      this.baseSiteService.getActive(),
    ])
      .pipe(first(), untilDestroyed(this))
      .subscribe(([userId, cartState, baseSiteId]) => {
        const cartId = this.activeCartService.getCartIdByUserId(cartState, userId);
        this.dcpApiService
          .callKyma<Cart>(
            'cartDecorator',
            'logPaymentError',
            {},
            {
              body,
              params: {
                cartId,
                userId,
                baseSiteId,
              },
            },
            'POST'
          )
          .pipe(first(), untilDestroyed(this))
          .subscribe((cart) => {});
      });
  }

  isAuthenticationSuccessful(authResponse: DelegoRapidPayThreeDSecureInfoWithDelegoToken, cardType: string): boolean {
    return this.delegoAuthenticationValidationService.isAuthenticationValid(authResponse, cardType);
  }

  isAuthorizationSuccessful(authorizeResponse: DelegoRapidPayAuthorization): boolean {
    return authorizeResponse?.authorized;
  }

  getMessageForDelego(state: DelegoPaymentState): Observable<IDcpGlobalErrorMessage> {
    const delegoResponseError =
      state.error ||
      this.getPaymentErrorsByConsignmentAuthenticationResponses(state) ||
      this.getPaymentErrorsByConsignmentAuthorizationResponses(state);
    const paymentError: PaymentError = state.error || delegoResponseError || PaymentError.PAYMENT_ERROR_RESPONSE;
    return this.getDisposableMessageByPaymentError(paymentError);
  }

  private getPaymentErrorsByConsignmentAuthenticationResponses(state: DelegoPaymentState): PaymentError | undefined {
    const responseStatuses = state.consignmentCodes
      .map((consignmentCode) => state.data[consignmentCode])
      .map((data) => data.delegoAuthenticationResponse?.status)
      .filter(Boolean);
    return responseStatuses.map((status) => this.getPaymentErrorByAuthenticationResponse(status)).find(Boolean);
  }

  private getPaymentErrorByAuthenticationResponse(authenticationResponseCode: string) {
    return this.FAILURE_STATUSES.includes(authenticationResponseCode) ? PaymentError.THREEDS_ERROR : undefined;
  }

  private getPaymentErrorsByConsignmentAuthorizationResponses(state: DelegoPaymentState): PaymentError {
    const responseCodes = state.consignmentCodes
      .map((consignmentCode) => state.data[consignmentCode])
      .map((data) => data.delegoAuthorizationResponse?.responseCode);
    return responseCodes.map(this.getPaymentErrorByAuthorizationResponseCode).find(Boolean);
  }

  private getPaymentErrorByAuthorizationResponseCode(authorizationResponseCode: string): PaymentError | undefined {
    switch (authorizationResponseCode) {
      case CC_LIMIT_EXCEEDED_RESPONSE_CODE:
        return PaymentError.CC_LIMIT_EXCEEDED;
      default:
        return undefined;
    }
  }

  private getDisposableMessageByPaymentError(error: PaymentError): Observable<IDcpGlobalErrorMessage> {
    switch (error) {
      case PaymentError.COULD_NOT_GET_DELEGO_TOKEN:
        return this.translationService.translate(PaymentErrorMessage.systemErrorTryAgainLater).pipe(
          first(),
          map((message) => this.getDisposableMessage(message, GlobalMessageType.MSG_TYPE_ERROR)),
          untilDestroyed(this)
        );
      case PaymentError.COULD_NOT_GET_MERCHANT_ID:
        return this.translationService.translate(PaymentErrorMessage.issueWithProduct).pipe(
          first(),
          map((message) => this.getDisposableMessage(message, GlobalMessageType.MSG_TYPE_ERROR)),
          untilDestroyed(this)
        );
      case PaymentError.CC_LIMIT_EXCEEDED:
        return this.translationService.translate(PaymentErrorMessage.ccLimitExceeded).pipe(
          first(),
          map((message) =>
            this.getDisposableMessage(message, GlobalMessageType.MSG_TYPE_ERROR, {
              isShown: true,
              id: 'edit-button',
              buttonText: 'checkoutReview.error.ccLimitExceeded.button',
              action: () => {
                this.refreshCartService.setEditCartModalOpen(false);
                this.router.navigate(['/checkout/data-entry'], {
                  queryParams: {paymentType: 'ACCOUNT'},
                  queryParamsHandling: 'merge',
                });
              },
            })
          ),
          untilDestroyed(this)
        );
      case PaymentError.PAYMENT_ERROR_RESPONSE:
        return this.translationService.translate(PaymentErrorMessage.paymentRejected).pipe(
          first(),
          map((message) => this.getDisposableMessage(message, GlobalMessageType.MSG_TYPE_ERROR)),
          untilDestroyed(this)
        );
      case PaymentError.PAYMENT_INFO_NOT_SET:
        return this.translationService.translate(PaymentErrorMessage.paymentRejected).pipe(
          first(),
          map((message) => this.getDisposableMessage(message, GlobalMessageType.MSG_TYPE_ERROR)),
          untilDestroyed(this)
        );
      case PaymentError.THREEDS_ERROR:
        return this.translationService.translate(PaymentErrorMessage.threeDsFailure).pipe(
          first(),
          map((message) => this.getDisposableMessage(message, GlobalMessageType.MSG_TYPE_ERROR)),
          untilDestroyed(this)
        );
      default:
        return this.translationService.translate(PaymentErrorMessage.somethingWrong).pipe(
          first(),
          map((message) => this.getDisposableMessage(message, GlobalMessageType.MSG_TYPE_ERROR)),
          untilDestroyed(this)
        );
    }
  }

  getErrorCodeAndMessage(): Observable<CommonError> {
    return this.getPaymentState().pipe(
      map((paymentState) => {
        if (paymentState && !paymentState.loading && paymentState.consignmentCodes) {
          for (const code of paymentState.consignmentCodes) {
            const data = paymentState.data[code];
            if (!this.isAuthenticationSuccessful(data.delegoAuthenticationResponse, paymentState.cardType)) {
              const auth = data.delegoAuthenticationResponse;
              return {
                errorCode: `ThreeDSecureStatus-${auth.status}`,
                errorMessage: 'ThreeDSecureStatusReason: ' + auth.statusReason,
              };
            } else if (!this.isAuthorizationSuccessful(data.delegoAuthorizationResponse)) {
              const auth = data.delegoAuthorizationResponse;
              return {
                errorCode: `TransactionStatus-${auth.transactionStatus}`,
                errorMessage: `ResponseDescription: ${auth.responseDescription}`,
              };
            }
          }
        }
        return undefined;
      })
    );
  }

  private getDisposableMessage(
    message: string,
    type: GlobalMessageType,
    button?: IDcpGlobalErrorMessageButton
  ): IDcpGlobalErrorMessage {
    return {
      message,
      type,
      button,
    };
  }

  private getPaymentState(): Observable<DelegoPaymentState> {
    return this.store.select(fromDelego.getState).pipe(untilDestroyed(this));
  }
}
